import type { DrpFormat, DrpChecklistRow, EducationChecklistRow } from "common/models/types";
import { eventLog } from "utils/AmplitudeUtil";
import {
  normalizeForMatch,
  DRP_HEADING,
  EDUCATION_HEADING,
  LIST_HEADING,
  COLUMN,
  LIMITS,
} from "utils/drpChecklistConstants";

/**
 * DRP 체크 추출(spec 설계 결정 5).
 *
 * 입력은 화면 뷰어(`MarkdownViewer`, referenceMode="button")가 그린 DOM 노드.
 * 출력은 항상 같은 체크 행 목록 + 형식 판정. 결과는 체크 전(`checked: false`) 상태다.
 *
 * reason: 통합약물관리(cmm_2)·요양시설(lcmm)에서 none이 나올 때 운영 신호용(`logDrpExtractOutcome`).
 */
export interface DrpChecklistResult {
  drpFormat: DrpFormat;
  drpItems: DrpChecklistRow[];
  educationItems: EducationChecklistRow[];
  reason?: "no_section" | "missing_column";
}

const BLOCK_TAGS = new Set([
  "P", "DIV", "LI", "BR", "TR", "TABLE", "THEAD", "TBODY", "UL", "OL",
  "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6",
]);

/**
 * 블록 경계를 공백 하나로 이어 텍스트를 모은다(설계 결정 5 — jsdom에 innerText가 없어 단위 테스트와
 * 브라우저가 같게). `<p>a</p><p>b</p>` → "a b".
 */
function collectText(node: Node): string {
  let out = "";
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3 /* TEXT_NODE */) {
      out += child.textContent ?? "";
    } else if (child.nodeType === 1 /* ELEMENT_NODE */) {
      const el = child as Element;
      const tag = el.tagName;
      if (tag === "BR") {
        out += " ";
      } else if (BLOCK_TAGS.has(tag)) {
        out += " " + collectText(el) + " ";
      } else {
        out += collectText(el);
      }
    }
  });
  return out;
}

/** 코드포인트 단위 절단(서로게이트 쌍을 반으로 자르지 않게). */
function truncateByCodePoint(s: string, max: number): string {
  const cps = Array.from(s);
  return cps.length <= max ? s : cps.slice(0, max).join("");
}

/** 첫 이모지 1개(VS16 포함, 없으면 빈 문자열). */
function firstEmoji(s: string): string {
  const m = s.match(/\p{Extended_Pictographic}️?/u);
  return m ? m[0] : "";
}

/**
 * 셀 텍스트 공통 정리: ⑆로 시작하는 토큰·빈/구분자 중괄호를 지우고 공백·끝 마침표 앞 공백을 정리한다.
 * (data-reference-id·data-drug-container·data-badge 요소는 호출 전에 DOM에서 이미 제거됐다.)
 */
function cleanCellText(raw: string): string {
  let t = raw;
  // ⑆CODE·⑆상담필요 등(한글 토큰 포함) — ⑆와 뒤따르는 토큰 문자 제거(공백·중괄호·괄호·콤마에서 멈춤).
  t = t.replace(/⑆[^\s{}(),]*/g, "");
  // 빈 중괄호·구분자만 남은 중괄호: {}, { }, {,}, {, }, { , }
  t = t.replace(/\{[\s,]*\}/g, "");
  // 공백 정리
  t = t.replace(/\s+/g, " ").trim();
  // 끝 마침표 앞 공백
  t = t.replace(/\s+\.(\s*)$/, ".");
  return t;
}

/** 패싯된 root를 복제한 뒤 추출용 노이즈 요소(참조·약이미지·배지)를 제거한다. */
function prepareWorkingRoot(root: Element): Element {
  const work = root.cloneNode(true) as Element;
  work
    .querySelectorAll("[data-reference-id], [data-drug-container], [data-badge]")
    .forEach((el) => el.remove());
  return work;
}

/** 문서 순서 평면 목록에서 제목 구역(제목 ~ 다음 제목 전)의 첫 table을 찾는다. */
function firstTableInRegion(flat: Element[], headingIndex: number): HTMLTableElement | null {
  for (let i = headingIndex + 1; i < flat.length; i++) {
    const el = flat[i];
    const tag = el.tagName;
    if (tag === "TABLE") return el as HTMLTableElement;
    if (tag === "H1" || tag === "H2" || tag === "H3") return null; // 같은 수준 이상 제목에서 멈춤
  }
  return null;
}

type ColumnIndex = { no: number; severity: number; type: number; summary: number };

/** 표 머리글에서 열 인덱스를 찾는다. summary 열을 못 찾으면 null(= 못 읽음). */
function resolveColumns(table: HTMLTableElement, kind: "drp" | "education"): ColumnIndex | null {
  let headerRow: Element | null = table.querySelector("thead tr");
  if (!headerRow) headerRow = table.querySelector("tr");
  if (!headerRow) return null;

  let headerCells = Array.from(headerRow.querySelectorAll("th"));
  if (headerCells.length === 0) headerCells = Array.from(headerRow.querySelectorAll("td"));

  const idx: ColumnIndex = { no: -1, severity: -1, type: -1, summary: -1 };
  const summaryTarget = kind === "drp" ? COLUMN.drpSummary : COLUMN.educationSummary;

  headerCells.forEach((cell, i) => {
    const norm = normalizeForMatch(collectText(cell));
    if (norm === COLUMN.no) idx.no = i;
    if (norm.includes(COLUMN.severity)) idx.severity = i;
    if (norm.includes(COLUMN.type)) idx.type = i;
    if (norm.includes(summaryTarget)) idx.summary = i;
  });

  if (idx.summary === -1) return null; // 필수 열 없음 → 못 읽음
  return idx;
}

/** 표 본문 행을 읽는다. */
function readTableRows(
  table: HTMLTableElement,
  cols: ColumnIndex,
  kind: "drp" | "education",
): Array<DrpChecklistRow | EducationChecklistRow> {
  let headerRow: Element | null = table.querySelector("thead tr");
  if (!headerRow) headerRow = table.querySelector("tr");

  const dataRows = Array.from(table.querySelectorAll("tr")).filter(
    (tr) => tr !== headerRow && tr.querySelectorAll("td").length > 0,
  );

  const rows: Array<DrpChecklistRow | EducationChecklistRow> = [];
  for (const tr of dataRows) {
    const cells = Array.from(tr.querySelectorAll("td"));
    const at = (i: number): string => (i >= 0 && cells[i] ? collectText(cells[i]) : "");

    const no = truncateByCodePoint(cleanCellText(at(cols.no)), LIMITS.no);
    const type = truncateByCodePoint(cleanCellText(at(cols.type)), LIMITS.type);
    const summary = truncateByCodePoint(cleanCellText(at(cols.summary)), LIMITS.summary);

    if (kind === "drp") {
      rows.push({
        no,
        severity: firstEmoji(cleanCellText(at(cols.severity))),
        type,
        summary,
        checked: false,
      });
    } else {
      rows.push({ no, type, summary, checked: false });
    }
  }
  return rows;
}

/** summary가 빈 행 제외 + 배열당 50행 초과 절단. */
function normalizeRows<T extends { summary: string }>(rows: T[]): T[] {
  return rows.filter((r) => r.summary.length > 0).slice(0, LIMITS.maxRows);
}

/** 목록 형식 추출(예비 — 건서페는 표 형식). */
function extractListFormat(flat: Element[], work: Element): DrpChecklistRow[] | null {
  const h2s = Array.from(work.querySelectorAll("h2"));
  const listH2 = h2s.find((h) => normalizeForMatch(collectText(h)).includes(LIST_HEADING));
  if (!listH2) return null;

  // 구역 = listH2 이후 다음 h2 전까지의 최상위 ol/ol·ul.
  const headingFlat = Array.from(work.querySelectorAll("h1,h2,h3,ol,ul"));
  const startIdx = headingFlat.indexOf(listH2);
  const regionLists: Element[] = [];
  for (let i = startIdx + 1; i < headingFlat.length; i++) {
    const el = headingFlat[i];
    if (el.tagName === "H1" || el.tagName === "H2") break;
    // 최상위(다른 li 안에 중첩되지 않은) 목록만.
    if ((el.tagName === "OL" || el.tagName === "UL") && !el.closest("li")) {
      regionLists.push(el);
    }
  }

  const rows: DrpChecklistRow[] = [];
  const ols = regionLists.filter((l) => l.tagName === "OL");
  const topLists = ols.length > 0 ? ols : regionLists.filter((l) => l.tagName === "UL");

  for (const list of topLists) {
    const items = Array.from(list.children).filter((c) => c.tagName === "LI") as Element[];
    for (const li of items) {
      const title = cleanCellText(directText(li));
      const subs = subItemTexts(li);
      let summary: string;
      if (list.tagName === "OL") {
        summary = subs.length > 0 ? `${title} — ${subs.join(" / ")}` : title;
      } else {
        summary = subs.length > 0 ? [title, ...subs].join(" / ") : title;
      }
      rows.push({
        no: String(rows.length + 1),
        severity: "",
        type: "",
        summary: truncateByCodePoint(cleanCellText(summary), LIMITS.summary),
        checked: false,
      });
    }
  }
  return rows;
}

/** li의 직계 텍스트(중첩 ul/ol 제외). */
function directText(li: Element): string {
  let out = "";
  li.childNodes.forEach((child) => {
    if (child.nodeType === 3) out += child.textContent ?? "";
    else if (child.nodeType === 1) {
      const el = child as Element;
      if (el.tagName === "UL" || el.tagName === "OL") return;
      out += collectText(el);
    }
  });
  return out;
}

/** li 안 중첩 목록의 항목 텍스트들. */
function subItemTexts(li: Element): string[] {
  const nested = Array.from(li.querySelectorAll(":scope > ul > li, :scope > ol > li"));
  return nested.map((sub) => cleanCellText(directText(sub))).filter((t) => t.length > 0);
}

/**
 * DRP 체크 추출 메인.
 */
export function extractDrpChecklist(root: Element): DrpChecklistResult {
  const work = prepareWorkingRoot(root);
  const flat = Array.from(work.querySelectorAll("h1,h2,h3,table"));

  const findHeading = (marker: string): number =>
    flat.findIndex(
      (el) => el.tagName === "H3" && normalizeForMatch(collectText(el)).includes(marker),
    );

  let drpItems: DrpChecklistRow[] = [];
  let educationItems: EducationChecklistRow[] = [];
  let drpReadable = false;
  let eduReadable = false;
  let anyTableFound = false;
  let missingColumn = false;

  const drpIdx = findHeading(DRP_HEADING);
  if (drpIdx >= 0) {
    const table = firstTableInRegion(flat, drpIdx);
    if (table) {
      anyTableFound = true;
      const cols = resolveColumns(table, "drp");
      if (cols) {
        drpItems = normalizeRows(readTableRows(table, cols, "drp") as DrpChecklistRow[]);
        drpReadable = true;
      } else {
        missingColumn = true;
      }
    }
  }

  const eduIdx = findHeading(EDUCATION_HEADING);
  if (eduIdx >= 0) {
    const table = firstTableInRegion(flat, eduIdx);
    if (table) {
      anyTableFound = true;
      const cols = resolveColumns(table, "education");
      if (cols) {
        educationItems = normalizeRows(
          readTableRows(table, cols, "education") as EducationChecklistRow[],
        );
        eduReadable = true;
      } else {
        missingColumn = true;
      }
    }
  }

  if (drpReadable || eduReadable) {
    return { drpFormat: "table", drpItems, educationItems };
  }
  if (anyTableFound && missingColumn) {
    return { drpFormat: "none", drpItems: [], educationItems: [], reason: "missing_column" };
  }

  // 표가 전혀 없으면 목록 형식 시도.
  const listRows = extractListFormat(flat, work);
  if (listRows && listRows.length > 0) {
    return { drpFormat: "list", drpItems: normalizeRows(listRows), educationItems: [] };
  }

  return { drpFormat: "none", drpItems: [], educationItems: [], reason: "no_section" };
}

/**
 * 추출 결과를 운영 신호(Amplitude)로 남기는 래퍼(설계 결정 5).
 * cmm_2·lcmm에서 none(못 읽음 포함)이면 1회, table/list면 0회, 다른 promptType이면 0회.
 */
export function logDrpExtractOutcome(
  promptType: string | undefined,
  reviewId: number | null | undefined,
  result: DrpChecklistResult,
): void {
  if (promptType !== "cmm_2" && promptType !== "lcmm") return;
  if (result.drpFormat === "none") {
    eventLog("consultation_drp_extract_none", {
      reviewId,
      promptType,
      reason: result.reason ?? "no_section",
    });
  }
}
